"use client";

import { Input } from "@heroui/input";
import { useEffect, useRef, useState } from "react";

import { SearchIcon } from "@/components/icons";

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible name of the search field. */
  label?: string;
  debounceMs?: number;
}

/**
 * Search field that reports changes after the user pauses typing.
 * Follows external changes to `value` (e.g. back navigation) without
 * clobbering what the user is currently typing.
 */
export function SearchBar({
  value,
  onChange,
  placeholder,
  label,
  debounceMs = 300,
}: SearchBarProps) {
  const [draft, setDraft] = useState(value);
  const [lastValue, setLastValue] = useState(value);
  const [lastEmitted, setLastEmitted] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Adopt external changes during render (no effect, no extra commit).
  // Our own debounced updates echo back as `value` and are ignored.
  if (value !== lastValue) {
    setLastValue(value);
    if (value !== lastEmitted) {
      setDraft(value);
      setLastEmitted(value);
    }
  }

  useEffect(() => {
    return () => clearTimeout(timer.current);
  }, []);

  const emit = (next: string) => {
    clearTimeout(timer.current);
    setLastEmitted(next);
    onChange(next);
  };

  const handleValueChange = (next: string) => {
    setDraft(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => emit(next), debounceMs);
  };

  return (
    <Input
      isClearable
      aria-label={label ?? placeholder}
      classNames={{
        base: "w-full",
        inputWrapper: "h-14",
      }}
      id="movie-search"
      name="search"
      placeholder={placeholder}
      startContent={
        <SearchIcon
          aria-hidden="true"
          className="pointer-events-none flex-shrink-0 text-base text-default-400"
        />
      }
      type="search"
      value={draft}
      onClear={() => {
        setDraft("");
        emit("");
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") emit(draft);
      }}
      onValueChange={handleValueChange}
    />
  );
}
